import { format } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import { BillItem, CouponType, ErrorCodes, JSONResponse, Packages } from '../types/billing';
import { Status } from '../types/status';
import { NON_BREAKING_HYPHEN } from './general';

export const CENT = 1;
export const DOLLAR = 100 * CENT;

export const INPUT_ZIP_CODE_INVALID_ERROR_MESSAGE = 'Please enter a valid zipcode.';

export const ZIP_CODE_LENGTH = 5;
export const ZIP_CODE_CHECK = /(^\d{5}$)/;

export const ONLY_DIGITS_REGEX = /^\d+$/;
export const ONLY_NUMBERS_REGEX = /^[0-9]+$/;
export const ONLY_LETTERS_REGEX = /[a-zA-Z]/;

export const DEFAULT_TRIAL_DURATION_DAYS = 7;

export const INPUT_TIMEOUT_UNTIL_VALIDATION_MS = 300;

export const DEFAULT_CANCEL_DATE_FORMAT = 'M/D/YY';

// SC NOTE: There is now a distinction in marketing vs support issued coupon types.
// marketing coupons are now refered to as "promotions", and will have the discounted price
// reflected in the package price, without need for explicit display of line item discount.
export const SUPPORT_ISSUED_DISCOUNTS = new Set([CouponType.AMOUNT, CouponType.PERCENT]);
export const MARKETING_ISSUED_DISCOUNTS = new Set([CouponType.PRICE]);
export const TRIAL_TYPE_COUPONS = new Set([CouponType.TRIAL, CouponType.EXTENSION]);

export const SUMMARY_TAXES_MSG = 'Excluding taxes and fees, where applicable.';

export const ADDONS_PROMOTIONS_FULL_TERMS_URL = 'https://philo.com/terms#philo-addon-promotions';

export const ADD_ON_MODAL_BILLING_MESSAGE =
  'To select this add-on, please provide your billing information.';
export const ADD_ON_MODAL_DELINQUENT_BILLING_MESSAGE =
  'To remove this add-on, please provide valid billing information.';
export const TMO_DISCLAIMER =
  'Please provide the primary mobile number or email associated with your eligible T-Mobile account to ensure on-going access to this offer.';
export const TMO_ERROR_MESSAGE =
  'Must be subscribed to the Philo Unlimited 1-year DVR $25/month plan to apply coupon.';

export const HUB_PACKAGE_WITH_ADDONS_NOT_ALLOWED_MESSAGE =
  'Sorry! The Philo + T-Mobile Hub Offer is not applicable with add-ons!';

// For various static text areas that display our pricing.  This text would be replaced with an actual value on render.
export const BASE_PRICE_PLACEHOLDER = '--BASE_PRICE_PLACEHOLDER--';
export const BASE_NUM_CHANNELS_PLACEHOLDER = '--BASE_NUM_CHANNELS_PLACEHOLDER--';

export const PHILO_LEGACY_PLAN = 'Philo Legacy';
export const PHILO_CORE_PLAN = 'Philo Core';
export const PHILO_FREE_PLAN = 'Free Channels';

export const PHILO_CORE_PLANS = new Set([
  Packages.PHILO_ARGON,
  Packages.PHILO_BORON,
  Packages.PHILO_CARBON,
]);

export const PHILO_LEGACY_PLANS = new Set([
  Packages.PHILO_2021,
  Packages.PHILO_2021_2m,
  Packages.PHILO_2021_3m,
  Packages.PHILO_PLUS,
]);

export const AD_FREE_UPGRADE_ADD_ONS = new Set([Packages.AMC_PLUS]);

export const PREMIUM_CORE_ADD_ONS = new Set([Packages.EPIX, Packages.MOVIE, Packages.STARZ]);

export type LegacyPlans = Extract<Packages, Packages.PHILO_2021 | Packages.PHILO_PLUS>;

export const PHILO_BASE_BENEFIT = `Unlimited 1${NON_BREAKING_HYPHEN}year DVR`;
export const PHILO_LEGACY_BENEFIT = `Unlimited 30${NON_BREAKING_HYPHEN}day DVR`;
export const PHILO_FREE_BENEFIT = `30${NON_BREAKING_HYPHEN}day DVR`;
export const PHILO_CORE_PLAN_PERK = `Includes AMC+ (ad-supported)`;

export const PHILO_FREE_CTA = 'Watch free channels';

interface PackageDisplayStrings {
  displayName: string;
  planPricePerUnit: string;
  corePlanPerk?: string;
  benefitDescription?: string;
  roundedChannelsCount?: string;
  totalChannelsCount?: number;
  shortDescription?: string;
  longDescription?: string;
}

export function getRoundedCountText(count: number) {
  return `${Math.floor(count / 10) * 10}+`;
}

export function getPackageDisplayStrings({
  item = {},
  preferredChannelCount = '',
}: {
  item: BillItem | Record<string, any>;
  preferredChannelCount?: string;
}): PackageDisplayStrings {
  const {
    id: packageId,
    displayName,
    channels = [],
    freeChannels = [],
    unitCount,
    basePriceCents,
    priceCents,
    unit,
  } = item;

  const channelsCount = channels.length;
  const totalChannelsCount = channelsCount + freeChannels.length;
  const planPricePerUnit = `$${basePriceCents / DOLLAR} / ${unit}`;
  const CHANNELS_ROUNDED_COUNT = getRoundedCountText(channelsCount);
  const TOTAL_CHANNELS_ROUNDED_COUNT = getRoundedCountText(totalChannelsCount);
  const PHILO_PLAN_GENERAL_COUNT = preferredChannelCount || CHANNELS_ROUNDED_COUNT;

  const LEGACY_SHORT_DESCRIPTION = `${PHILO_PLAN_GENERAL_COUNT} channels`;
  const LEGACY_LONG_DESCRIPTION = `${PHILO_PLAN_GENERAL_COUNT} channels with ${PHILO_LEGACY_BENEFIT}`;

  const FAST_LONG_DESCRIPTION = `Sign up for a free Philo account today— there's no credit card required. You
    can enjoy content from ${TOTAL_CHANNELS_ROUNDED_COUNT} channels, ${PHILO_FREE_BENEFIT}, and
    personalize your experience by saving shows and favoriting channels.`;

  const packageDisplayStrings: Record<string, PackageDisplayStrings> = {
    [Packages.PHILO_ARGON]: {
      displayName: PHILO_CORE_PLAN,
      corePlanPerk: PHILO_CORE_PLAN_PERK,
      benefitDescription: PHILO_BASE_BENEFIT,
      planPricePerUnit,
      roundedChannelsCount: PHILO_PLAN_GENERAL_COUNT,
      totalChannelsCount,
      shortDescription: `${PHILO_PLAN_GENERAL_COUNT} channels`,
      longDescription: `${PHILO_PLAN_GENERAL_COUNT} channels with ${PHILO_BASE_BENEFIT}`,
    },
    [Packages.PHILO_BORON]: {
      displayName: PHILO_CORE_PLAN,
      benefitDescription: 'TBD',
      planPricePerUnit,
      roundedChannelsCount: PHILO_PLAN_GENERAL_COUNT,
      totalChannelsCount,
      shortDescription: 'TBD',
      longDescription: 'TBD',
    },
    [Packages.PHILO_CARBON]: {
      displayName: PHILO_CORE_PLAN,
      benefitDescription: 'TBD',
      planPricePerUnit,
      roundedChannelsCount: PHILO_PLAN_GENERAL_COUNT,
      totalChannelsCount,
      shortDescription: 'TBD',
      longDescription: 'TBD',
    },
    [Packages.PHILO_2021]: {
      displayName: PHILO_LEGACY_PLAN,
      benefitDescription: PHILO_BASE_BENEFIT,
      planPricePerUnit,
      roundedChannelsCount: PHILO_PLAN_GENERAL_COUNT,
      totalChannelsCount,
      shortDescription: `${PHILO_PLAN_GENERAL_COUNT} channels`,
      longDescription: `${PHILO_PLAN_GENERAL_COUNT} channels with ${PHILO_BASE_BENEFIT}`,
    },
    [Packages.PHILO_2021_2m]: {
      displayName: `Prepay ${unitCount} months`,
      planPricePerUnit,
    },
    [Packages.PHILO_2021_3m]: {
      displayName: `${PHILO_LEGACY_PLAN}`,
      benefitDescription: PHILO_BASE_BENEFIT,
      planPricePerUnit: `$${priceCents / DOLLAR} every ${unitCount} months`,
      roundedChannelsCount: PHILO_PLAN_GENERAL_COUNT,
      totalChannelsCount,
      shortDescription: `${PHILO_PLAN_GENERAL_COUNT} channels`,
      longDescription: `${PHILO_PLAN_GENERAL_COUNT} channels with ${PHILO_BASE_BENEFIT}`,
    },
    [Packages.PHILO_PLUS]: {
      displayName: PHILO_LEGACY_PLAN,
      benefitDescription: PHILO_LEGACY_BENEFIT,
      planPricePerUnit,
      shortDescription: LEGACY_SHORT_DESCRIPTION,
      longDescription: LEGACY_LONG_DESCRIPTION,
    },
    [Packages.PHILO_FAST]: {
      displayName: PHILO_FREE_PLAN,
      benefitDescription: PHILO_FREE_BENEFIT,
      planPricePerUnit,
      roundedChannelsCount: TOTAL_CHANNELS_ROUNDED_COUNT,
      totalChannelsCount,
      shortDescription: `${TOTAL_CHANNELS_ROUNDED_COUNT} channels`,
      longDescription: FAST_LONG_DESCRIPTION,
    },
    [Packages.EPIX]: {
      displayName,
      planPricePerUnit,
      longDescription: `${displayName} add-on`,
    },
    [Packages.STARZ]: {
      displayName,
      planPricePerUnit,
      longDescription: `${displayName} add-on`,
    },
    [Packages.MOVIE]: {
      displayName,
      planPricePerUnit,
      longDescription: `${displayName} add-on`,
    },
    [Packages.AMC_PLUS]: {
      displayName,
      planPricePerUnit,
      longDescription: 'TBD',
    },
    [Packages.BET_PLUS]: {
      displayName,
      planPricePerUnit,
      longDescription: 'TBD',
    },
    [Packages.ALLBLK_PLUS]: {
      displayName,
      planPricePerUnit,
      longDescription: 'TBD',
    },
  };

  return packageDisplayStrings[packageId] || {};
}

export function checkResponse(response: Response) {
  const isNon200Response = !response.ok;
  if (isNon200Response) {
    const message = `${response.status}: ${response.statusText}`;
    // JD: Build up a JSON object similar to what
    // the billing API returns and pass it along.
    const substituteJSON = {
      success: false,
      errors: [{ message }],
      result: {},
    };
    return substituteJSON;
  }

  // JD: Response is in 200 range so convert to JSON and pass along.
  return response.json();
}

export function extractErrorMessage(jsonResponse: JSONResponse, defaultErrorMessage: string) {
  let errorMessage = '';
  let errorCode;
  let isSurge = false;

  const { errors, success } = jsonResponse || {};
  if (success) {
    return { errorMessage, errorCode, isSurge };
  }

  if (!isEmpty(errors)) {
    const { code, message } = errors[0] || {};
    errorCode = code;
    isSurge = code === ErrorCodes.ERROR_SURGE_REQUEST_ENQUEUED;
    if (!isSurge) {
      const codeText = !isNil(code) ? ` (Code: ${code})` : '';
      errorMessage = `${message}${codeText}`;
    }
  } else {
    errorMessage = defaultErrorMessage;
  }

  return { errorMessage, errorCode, isSurge };
}

export function formatBillingDate(utcDate: number | string) {
  const date = new Date(utcDate);
  const dd = date.getDate();
  const mm = date.getMonth() + 1;

  return `${mm}/${dd}`;
}

export function formatPromoEndDateLong(utcDate: Date | string) {
  const date = new Date(utcDate);
  const dd = date.getUTCDate();
  const mm = date.getUTCMonth() + 1;
  const yyyy = date.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
}

export function formatBillingAmountFromCents(amount: number) {
  if (amount || amount === 0) {
    const amountFromCents = amount / DOLLAR;
    return amountFromCents.toFixed(2);
  }
  return '';
}

export function formatBillingMonthDateRange(billDate: string) {
  const startingBillDate = new Date(billDate);
  const futureBillDate = startingBillDate.setMonth(startingBillDate.getMonth() + 1);
  return `(${formatBillingDate(billDate)} - ${formatBillingDate(futureBillDate)})`;
}

// Takes some copy and replaces any instances of BASE_PRICE_PLACEHOLDER text with specified price.
export function replacePlaceholderPlanValues({ value = '', price = '', numChannels = '' }) {
  if (!price && !numChannels) {
    return '';
  }

  return value
    ? value
        .replace(new RegExp(BASE_PRICE_PLACEHOLDER, 'g'), price || BASE_PRICE_PLACEHOLDER)
        .replace(
          new RegExp(BASE_NUM_CHANNELS_PLACEHOLDER, 'g'),
          numChannels || BASE_NUM_CHANNELS_PLACEHOLDER,
        )
    : value;
}

export function createCancelDate(trialDays: number, dateFormat = DEFAULT_CANCEL_DATE_FORMAT) {
  return format(Date.now() + 24 * 60 * 60 * 1000 * trialDays, dateFormat);
}

export function isDiscountTypeCoupon(benefitType?: CouponType) {
  if (!benefitType) {
    return false;
  }
  return SUPPORT_ISSUED_DISCOUNTS.has(benefitType) || MARKETING_ISSUED_DISCOUNTS.has(benefitType);
}

function hasLetterCharacters(str: string) {
  const isLetterFound = ONLY_LETTERS_REGEX.test(str);
  return isLetterFound;
}

export function normlizeZipCode(zipCode: string) {
  return zipCode.replace(ONLY_LETTERS_REGEX, '');
}

export function validateZipCode(zipCode: string) {
  if (
    isNil(zipCode) ||
    hasLetterCharacters(zipCode) ||
    zipCode.length < ZIP_CODE_LENGTH ||
    !ZIP_CODE_CHECK.test(zipCode)
  ) {
    return Status.INVALID;
  }

  return Status.VALID;
}
